import { createApp } from "vue";
import { createPinia } from "pinia";
import PrimeVue from "primevue/config";
import { definePreset } from "@primevue/themes";
import Aura from "@primevue/themes/aura";
import ToastService from "primevue/toastservice";
import ConfirmationService from "primevue/confirmationservice";
import Tooltip from "primevue/tooltip";

import "primeicons/primeicons.css";
import "./assets/styles.css";

import App from "./App.vue";
import router from "./router";
import { setOnAuthFailure } from "./api/client";
import { useAuthStore } from "./stores/auth";
import { useThemeStore } from "./stores/theme";

/*
 * Aura preset re-tuned to the Xellerate / Wavetec palette: azure primary
 * (#0284C7 / #0EA5E9) on white surfaces; the deep navy lives in the
 * AppShell sidebar (see assets/styles.css tokens).
 */
const XelleratePreset = definePreset(Aura, {
  semantic: {
    primary: {
      50: "#F0F9FF",
      100: "#E0F2FE",
      200: "#BAE6FD",
      300: "#7DD3FC",
      400: "#38BDF8",
      500: "#0EA5E9",
      600: "#0284C7",
      700: "#0369A1",
      800: "#075985",
      900: "#0C4A6E",
      950: "#082F49",
    },
    colorScheme: {
      light: {
        primary: {
          color: "{primary.600}",
          hoverColor: "{primary.700}",
          activeColor: "{primary.700}",
          contrastColor: "#ffffff",
        },
        highlight: {
          background: "{primary.50}",
          focusBackground: "{primary.100}",
          color: "{primary.700}",
          focusColor: "{primary.800}",
        },
      },
      dark: {
        primary: {
          color: "{primary.400}",
          hoverColor: "{primary.300}",
          activeColor: "{primary.300}",
          contrastColor: "{primary.950}",
        },
        highlight: {
          background: "color-mix(in srgb, {primary.400}, transparent 84%)",
          focusBackground: "color-mix(in srgb, {primary.400}, transparent 76%)",
          color: "rgba(255,255,255,.87)",
          focusColor: "rgba(255,255,255,.87)",
        },
      },
    },
  },
});

const app = createApp(App);
const pinia = createPinia();

app.use(pinia);

// Apply the persisted theme BEFORE mounting so the first paint is
// already light/dark (no flash). Also follows the OS in "system" mode.
useThemeStore(pinia).init();

app.use(router);
app.use(PrimeVue, {
  theme: {
    preset: XelleratePreset,
    options: {
      // PrimeVue components follow the app theme (stores/theme.ts sets
      // data-theme on <html>).
      darkModeSelector: '[data-theme="dark"]',
    },
  },
});
app.use(ToastService);
app.use(ConfirmationService);
app.directive("tooltip", Tooltip);

// When a token refresh fails, drop local auth state and go to /login.
setOnAuthFailure(() => {
  const auth = useAuthStore(pinia);
  auth.forceLogout();
  if (router.currentRoute.value.name !== "login") {
    router.push({ name: "login" });
  }
});

app.mount("#app");
